import React, {useContext, useEffect, useState} from "react";
import {apiContext} from "../../../ApiContext";
import SelectAccountsContainer from "../select-accounts/SelectAccountsContainer";
import FeedConfirmationResultContainer from "../feed-confirmation/openbanking/FeedConfirmationResultContainer";
import * as _ from "lodash";
import PropTypes from "prop-types";
import {endSpan, setSpanStatus, startSpan} from "../../../common/telemetry/OpenTelemetryHelper";
import SpanStatusCode from "../../../common/telemetry/SpanStatusCode";
import {safeTrack} from "../../../common/telemetry/SegmentHelper";
import {FullStory} from "@fullstory/browser";
import ErrorView from "../../../common/ErrorView";
import Loading from "../../../common/Loading";

function ExistingEditContainer({consentId}) {
  const api = useContext(apiContext)
  const [feedAccountDetails, setFeedAccountDetails] = React.useState({});
  const [errorType, setErrorType] = useState(null)
  const [isLoading, setIsLoading] = useState(true);
  const [isFeedUpdated, setIsFeedUpdated] = useState(false);
  const [accountsToOnboard, setAccountsToOnboard] = useState([]);

  useEffect(() => {
    const loadInOpenBankingFinaliseSpan = startSpan('load_in_fetching_open_banking_account_detail_span');
    const getAccountData = async () => {
      let accountData;
      try {
        accountData = await api.fetchAccountDataByConsentId(consentId);
        setFeedAccountDetails(accountData)
      } catch (err) {
        if (err.message !== 'Unauthorized') {
          setSpanStatus(loadInOpenBankingFinaliseSpan, {code: SpanStatusCode.ERROR});
          endSpan(loadInOpenBankingFinaliseSpan);
          setErrorType('openBankingFinaliseError');
        }
      }

      const accountsNotOnboarded = getAccountsNotOnboarded(accountData.accounts);
      if (accountData.availableFeeds == null || accountsNotOnboarded.length <= accountData.availableFeeds) {
        await updateFeedsInfo(accountData, accountsNotOnboarded, accountData.accounts, accountData.accountsToDisconnect, true);
        setIsFeedUpdated(true);
        setSpanStatus(loadInOpenBankingFinaliseSpan, {code: SpanStatusCode.OK});
        endSpan(loadInOpenBankingFinaliseSpan);
      }
      setIsLoading(false);
    }
    getAccountData();
  }, [consentId])

  const getAccountsNotOnboarded = (accounts) => {
    return _.filter(accounts, account => !account.onboarded && !account.unsupportedDescription);
  }

  const accountsAlreadyOnboarded = (accounts) => {
    return _.filter(accounts, account => account.onboarded);
  }

  const sendEventForUpdatedFeeds = (accountData, accountsToOnboard, accountsToExtend, isAutoSubmitted) => {
    const eventName = isAutoSubmitted ? 'elementViewed' : 'elementClicked';
    if (accountsToOnboard && accountsToOnboard.length > 0) {
      safeTrack(
        eventName, {
          action: 'created_feeds_successfully',
          page: 'existing/edit',
          businessId: accountData.consumptionId,
          source: 'OpenBanking',
          consumer: 'Solo',
          applicationId: accountData.applicationId
        }
      )
    }
    if (accountsToExtend && accountsToExtend.length > 0) {
      safeTrack(
        eventName, {
          action: 'extended_feeds_successfully',
          page: 'existing/edit',
          businessId: accountData.consumptionId,
          source: 'OpenBanking',
          consumer: 'Solo',
          applicationId: accountData.applicationId
        }
      )
    }
  }

  async function updateFeedsInfo(accountData, accountsToOnboard, accounts, accountsToDisconnect, isAutoSubmitted) {
    setIsLoading(true);
    setAccountsToOnboard(accountsToOnboard);

    const accountsToExtend = accountsAlreadyOnboarded(accounts);
    const accountsToOnboardOrExtend = [...accountsToOnboard, ...accountsToExtend]
    try {
      await api.updateFeeds(accountData.applicationId, accountsToOnboardOrExtend, accountsToDisconnect);
      sendEventForUpdatedFeeds(accountData, accountsToOnboard, accountsToExtend, isAutoSubmitted);
      setIsFeedUpdated(true);
    } catch (err) {
      setErrorType('openBankingFinaliseError');
    }
    setIsLoading(false);
  }

  const handleSubmit = async (selectedAccounts, accounts, accountsToDisconnect) => {
    setIsLoading(true);
    setAccountsToOnboard(selectedAccounts);

    try {
      await updateFeedsInfo(feedAccountDetails, selectedAccounts, accounts, accountsToDisconnect, false);
    } catch (err) {
      setErrorType('openBankingFinaliseError');
    }
    setIsLoading(false);
  };

  const renderError = () => {
    FullStory('trackEvent', {
      name: 'Onboard Finalise Error',
      properties: {
        error_code: errorType,
        source: "OpenBanking",
      },
    })
    return <ErrorView type={errorType}/>
  }

  function renderAccountReconsentView() {
    return isFeedUpdated ?
      <FeedConfirmationResultContainer
        source="OpenBanking"
        accounts={feedAccountDetails.accounts}
        selectedOnboardAccounts={accountsToOnboard}
        disconnectedAccounts={feedAccountDetails.accountsToDisconnect}
        consumerId="Solo"
      /> :
      <SelectAccountsContainer
        accounts={_.sortBy(feedAccountDetails.accounts, (account) => [!account.onboarded, account.unsupportedDescription])}
        bankfeedLimit={feedAccountDetails.bankfeedLimit}
        availableFeeds={feedAccountDetails.availableFeeds}
        subscriptionUpgradeUrl={feedAccountDetails.subscriptionUpgradeUrl}
        onSubmit={(selectedAccounts) => handleSubmit(selectedAccounts, feedAccountDetails.accounts, feedAccountDetails.accountsToDisconnect)}
        source="OpenBanking"
        setErrorType={setErrorType}
        consumerId="Solo"
        page="ExistingEdit"
      />;
  }

  return errorType ? renderError() :
    isLoading ?
      <Loading message={"Fetching your feed info..."}/>
      : renderAccountReconsentView()
}

ExistingEditContainer.propTypes = {
  consentId: PropTypes.string.isRequired,
}

export default ExistingEditContainer

